import { cn } from '@/@core/utils/cn'
import LinkWithTransition from '@/v2/components/helpers/link-with-transition'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import { Button } from '@/v2/components/ui/button'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { fadeIn, staggerContainer } from '@/v2/constants/motion'
import { motion } from 'framer-motion'
import bgImage from 'public/images/v2/pages/main-pages/snowboard-shipping/hero-bg.svg'
import React from 'react'

interface HeroSectionProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: string
  description?: string
  points?: string[]
  firstImage: { url: string; alt: string }
  secondImage: { url: string; alt: string }
  ctaButton: { href: string; text: string }
  img1ClassName?: string
  img2ClassName?: string
  btnClassName?: string
}

const HeroSection: React.FC<HeroSectionProps> = props => {
  const {
    title,
    description,
    points,
    firstImage,
    ctaButton,
    secondImage,
    className,
    img1ClassName,
    img2ClassName,
    btnClassName,
    ...rest
  } = props

  return (
    <motion.section
      variants={staggerContainer()}
      initial='hidden'
      whileInView='show'
      viewport={{ once: true, amount: 0.25 }}
      className='section-container bg-[#FFFFFF] section-spacing'
      style={{ backgroundImage: `url(${bgImage.src})` }}
    >
      <SectionWrapper {...rest} className='xl-min:!max-w-[1740px] lg-mn:max-w-[943.33px] md-mn:max-w-[735px]'>
        <div className={cn('flex items-center justify-between h-full w-full', className)}>
          <motion.div variants={fadeIn('left', 'tween', 0, 1)}>
            <ResponsiveImage priority src={firstImage.url} alt={firstImage.alt} className={img1ClassName} />
          </motion.div>
          <motion.div
            variants={fadeIn('right', 'tween', 0, 1)}
            className='flex flex-col items-start justify-center w-full [&>h1]:text-heading-home [&>h1]:!w-full [&>h1]:xl-min:!leading-[72px] [&>h1]:text-center'
          >
            <h1 dangerouslySetInnerHTML={{ __html: title }} />

            {description && (
              <p
                className={
                  'text-paragraph xl-min:!text-xl md-mn:text-base text-lg mt-6 font-normal leading-[30px] text-center mx-auto max-w-[323px] md-mn:max-w-full'
                }
              >
                {description}
              </p>
            )}

            {points && (
              <ul className='list-disc list-inside font-medium mt-5 md-mn:block hidden ml-3'>
                {points?.map(point => (
                  <li key={point} className='text-paragraph font-normal text-base xl-min:text-xl leading-[30px]'>
                    {point}
                  </li>
                ))}
              </ul>
            )}

            <LinkWithTransition href={ctaButton.href} className='self-center mt-9'>
              <Button
                variant='call_to_action'
                className={cn('transition-all animate-bounce hover:animated-paused', btnClassName)}
              >
                {ctaButton.text}
              </Button>
            </LinkWithTransition>
          </motion.div>

          <motion.div variants={fadeIn('left', 'tween', 0, 1)}>
            <ResponsiveImage priority src={secondImage.url} alt={secondImage.alt} className={img2ClassName} />
          </motion.div>
        </div>
      </SectionWrapper>
    </motion.section>
  )
}

export default HeroSection
